import PageBrand from '@components/common/PageBrand'
import Grid from '@mui/material/Grid'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTheme } from '@mui/material'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import BackgroundAnimation from '../components/common/BackgroundAnimation'
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import { Link, useHistory } from 'react-router-dom'
import { isValidEmail, sha256 } from '../utils/functions'
import { login, oauthLogin } from '../api/user'
import AppContext, { JWT } from '../app/AppContext'
import { jwtDecode } from 'jwt-decode'
import Alert, { AlertInput } from '../components/common/Alert'
import { GlassButton, GlassCard, GlassOutlineButton } from '@components/common/Glass'
import { Capacitor } from '@capacitor/core'
import { SocialLogin } from '@capgo/capacitor-social-login'
import { setAuthToken } from '../utils/helpers/authStorage'
import { mainColor } from 'src/ui/theme/tokens'

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (opts: {
            client_id: string
            callback: (res: { credential?: string }) => void
            ux_mode?: 'popup' | 'redirect'
          }) => void
          prompt: () => void
          renderButton: (
            el: HTMLElement,
            options: Record<string, string | number | boolean>
          ) => void
        }
      }
    }
  }
}

export default function Login(): JSX.Element {
  const theme = useTheme()
  const history = useHistory()

  const { appReducer } = useContext(AppContext)

  const [alert, setAlert] = useState<AlertInput>({ open: false })
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const isNative = Capacitor.isNativePlatform()
  const [googleWebReady, setGoogleWebReady] = useState<boolean>(false)
  const googleWebClientId = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID as string | undefined
  const googleButtonRef = useRef<HTMLDivElement | null>(null)
  const [authLoading, setAuthLoading] = useState(false)

  const inputSx = {
    '& .MuiInputBase-root': {
      background: '#ffffff',
      borderRadius: '14px',
      // border: '1px solid rgba(255,255,255,0.75)',
      boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
      backdropFilter: 'none',
    },
    '& .MuiInputBase-input': {
      py: 1.25,
    },
  } as const

  const handleSetJWT = (jwtString: string | undefined) => {
    if (!jwtString) return
    const jwtData = jwtDecode(jwtString) as JWT
    setAuthToken(jwtString, jwtData)
    appReducer({ type: 'jwt', payload: jwtData })
    setAuthLoading(false)
    history.push('/')
  }

  const handleSubmit = async () => {
    const hashPass = await sha256(password)

    if (!email || !password) {
      setAlert({
        open: true,
        severity: 'warning',
        message: 'Missing email or password',
      })
      return
    }

    // check email and password again
    if (!isValidEmail(email)) {
      setAlert({
        open: true,
        severity: 'warning',
        message: 'Invalid email.',
      })
      return
    } else {
      try {
        setAuthLoading(true)
        const result = await login(email, hashPass)
        if (result.success) {
          handleSetJWT(result.data)
        } else {
          setAlert({
            open: true,
            severity: 'error',
            message: result.message,
          })
          setAuthLoading(false)
        }
      } catch (e) {
        setAlert({
          open: true,
          severity: 'error',
          message: 'Encountered error',
        })
        setAuthLoading(false)
        return
      }
    }
  }

  const handleGoogleLogin = async () => {
    if (isNative) {
      console.log('IS NATIVE')
      try {
        setAuthLoading(true)
        const result = await SocialLogin.login({
          provider: 'google',
          options: {},
        })
        const idToken =
          (result as { result?: { idToken?: string } })?.result?.idToken ||
          (result as { result?: { authentication?: { idToken?: string } } })?.result?.authentication
            ?.idToken
        if (!idToken) {
          setAlert({
            open: true,
            severity: 'error',
            message: 'Google login failed.',
          })
          setAuthLoading(false)
          return
        }

        const res = await oauthLogin('google', idToken)
        if (!res.success) {
          setAlert({
            open: true,
            severity: 'error',
            message: res.message || 'Login failed.',
          })
          setAuthLoading(false)
          return
        }
        handleSetJWT(res.data)
      } catch (e) {
        setAlert({
          open: true,
          severity: 'error',
          message: 'Google login failed.',
        })
        setAuthLoading(false)
      }
      return
    }

    console.log('Web client')
    if (!googleWebClientId) {
      setAlert({
        open: true,
        severity: 'warning',
        message: 'Google web login is not configured.',
      })
      return
    }

    if (!googleWebReady) {
      setAlert({
        open: true,
        severity: 'info',
        message: 'Google login is still loading. Please try again.',
      })
      return
    }

    window.google?.accounts?.id?.prompt()
  }

  const handleAppleLogin = async () => {
    if (!isNative) {
      setAlert({
        open: true,
        severity: 'info',
        message: 'Apple login is available on the mobile app.',
      })
      return
    }

    try {
      setAuthLoading(true)
      const result = await SocialLogin.login({ provider: 'apple', options: {} })
      const idToken =
        (result as { result?: { idToken?: string } })?.result?.idToken ||
        (result as { result?: { identityToken?: string } })?.result?.identityToken
      if (!idToken) {
        setAlert({
          open: true,
          severity: 'error',
          message: 'Apple login failed.',
        })
        setAuthLoading(false)
        return
      }

      const res = await oauthLogin('apple', idToken)
      if (!res.success) {
        setAlert({
          open: true,
          severity: 'error',
          message: res.message || 'Login failed.',
        })
        setAuthLoading(false)
        return
      }
      handleSetJWT(res.data)
    } catch (e) {
      setAlert({
        open: true,
        severity: 'error',
        message: 'Apple login failed.',
      })
      setAuthLoading(false)
    }
  }

  useEffect(() => {
    if (isNative) return
    if (!googleWebClientId) return
    if (window.google?.accounts?.id) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setGoogleWebReady(true)
    } else {
      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.onload = () => setGoogleWebReady(true)
      script.onerror = () => setGoogleWebReady(false)
      document.head.appendChild(script)
    }
  }, [googleWebClientId, isNative])

  useEffect(() => {
    if (isNative) return
    if (!googleWebClientId) return
    if (!googleWebReady) return
    if (!googleButtonRef.current) return

    window.google?.accounts?.id?.initialize({
      client_id: googleWebClientId,
      ux_mode: 'popup',
      callback: async (res) => {
        const idToken = res.credential
        if (!idToken) {
          setAlert({
            open: true,
            severity: 'error',
            message: 'Google login failed.',
          })
          return
        }

        setAuthLoading(true)
        const apiRes = await oauthLogin('google', idToken)
        if (!apiRes.success) {
          setAlert({
            open: true,
            severity: 'error',
            message: apiRes.message || 'Login failed.',
          })
          setAuthLoading(false)
          return
        }
        handleSetJWT(apiRes.data)
      },
    })

    window.google?.accounts?.id?.renderButton(googleButtonRef.current, {
      theme: 'outline',
      size: 'large',
      width: '100%',
      text: 'continue_with',
      shape: 'pill',
    })
  }, [googleWebClientId, googleWebReady, isNative])

  return (
    <div>
      <BackgroundAnimation />
      {/* <img id="pencilImg" src="trophy.png" alt="" style={{ display: 'none' }} /> */}
      <Container
        sx={{
          minHeight: '100dvh',
          py: 3,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
        }}
        maxWidth="sm"
      >
        <PageBrand />
        <Stack width="100%" justifyContent="center" spacing={2}>
          <GlassCard
            sx={{
              pt: 1,
              pb: 1,
              px: { xs: 1.5, sm: 3.5 },
              borderRadius: '32px',
              borderColor: 'rgba(255,255,255,0.7)',
              boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {authLoading ? (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  background: 'rgba(255,255,255,0.65)',
                  backdropFilter: 'none',
                }}
              >
                <CircularProgress size={32} sx={{ color: mainColor }} />
                <Typography fontWeight={600} color="#0f172a">
                  Signing you in...
                </Typography>
              </Box>
            ) : null}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={0}>
              <GlassOutlineButton
                variant="outlined"
                onClick={() => history.push('/')}
                disabled={authLoading}
                sx={{
                  p: 1,
                  minWidth: 0,
                }}
              >
                <ArrowBackIosNewRoundedIcon fontSize="small" sx={{ mr: 0.25 }} />
              </GlassOutlineButton>
            </Box>
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                await handleSubmit()
              }}
            >
              <Grid container justifyContent="center" spacing={1} mt={0}>
                <Grid item xs={12}>
                  <Typography variant={fullScreen ? 'h4' : 'h4'} fontWeight={600}>
                    Welcome back
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.75}>
                    Logging in allows you to save your lists and participate in challenges.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Grid container justifyContent="center" spacing={2} mt={1}>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        fullWidth={true}
                        sx={inputSx}
                        placeholder="Email"
                        autoComplete="username"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      ></TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        fullWidth={true}
                        sx={inputSx}
                        placeholder="Password"
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      ></TextField>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} mt={2}>
                  <GlassButton
                    variant="contained"
                    size="large"
                    fullWidth={true}
                    disabled={authLoading}
                    sx={{
                      minHeight: 48,
                      fontSize: 16,
                    }}
                    type="submit"
                  >
                    Login
                  </GlassButton>
                </Grid>
                <Grid item xs={12} mt={1}>
                  <Divider></Divider>
                </Grid>
                {isNative ? (
                  <Grid item xs={12} mt={1}>
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth={true}
                      disabled={authLoading}
                      onClick={handleAppleLogin}
                      startIcon={
                        <Box
                          component="img"
                          src="/external/apple-logo.png"
                          alt="Apple"
                          sx={{ width: 18, height: 18 }}
                        />
                      }
                      sx={{
                        backgroundColor: '#ffffff',
                        color: '#162f49',
                        borderRadius: '18px',
                        height: '50px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
                        textTransform: 'none',
                        fontFamily:
                          'SF Pro Text, SF Pro Display, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                        '@media (hover: hover) and (pointer: fine)': {
                          '&:hover': {
                            backgroundColor: '#ffffff',
                            boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
                            transform: 'translateY(-1px)',
                          },
                        },
                      }}
                    >
                      Continue with Apple
                    </Button>
                  </Grid>
                ) : null}
                <Grid item xs={12} mt={0}>
                  {isNative ? (
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth={true}
                      disabled={authLoading}
                      onClick={handleGoogleLogin}
                      startIcon={
                        <Box
                          component="img"
                          src="/external/google-logo.png"
                          alt="Google"
                          sx={{ width: 18, height: 18 }}
                        />
                      }
                      sx={{
                        backgroundColor: '#ffffff',
                        color: '#1f2937',
                        borderRadius: '18px',
                        height: '50px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
                        textTransform: 'none',
                        fontFamily: 'Roboto, Arial, sans-serif',
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                        '@media (hover: hover) and (pointer: fine)': {
                          '&:hover': {
                            backgroundColor: '#ffffff',
                            boxShadow: '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709',
                            transform: 'translateY(-1px)',
                          },
                        },
                      }}
                    >
                      Continue with Google
                    </Button>
                  ) : (
                    <Box
                      sx={{
                        position: 'relative',
                      }}
                    >
                      <Box
                        ref={googleButtonRef}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          '& > div': { width: '100%' },
                        }}
                      />
                      {authLoading ? (
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '999px',
                            background: 'rgba(255,255,255,0.7)',
                          }}
                        />
                      ) : null}
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} mt={2}>
                  <Typography variant="body1" sx={{ fontSize: '16px', color: '#333' }}>
                    Don&apos;t have an account?{' '}
                    <Link to="/createAccount" style={{ fontWeight: 500, textDecoration: 'none' }}>
                      Create one
                    </Link>
                  </Typography>
                </Grid>
                <Grid item xs={12} mt={0.5}>
                  <Link
                    to="/forgotPassword"
                    style={{
                      textDecoration: 'none',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Forgot password?
                    </Typography>
                  </Link>
                </Grid>
              </Grid>
            </form>
          </GlassCard>

          <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
            <Link
              to="/privacy"
              style={{
                textDecoration: 'none',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 500,
                  color: '#60768b',
                  textDecoration: 'none;',
                }}
              >
                Privacy Policy
              </Typography>
            </Link>
            <Link
              to="/terms"
              style={{
                textDecoration: 'none',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 500,
                  color: '#60768b',
                  textDecoration: 'none;',
                }}
              >
                Terms of Service
              </Typography>
            </Link>
          </Box>
        </Stack>
      </Container>
      <Alert alert={alert} onClose={() => setAlert({ ...alert, open: false })} />
    </div>
  )
}
